import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { ArrowLeft } from "@phosphor-icons/react";
import AreaMultiPick from "../../components/admin/AreaMultiPick";

export default function OfficeStaffForm() {
  const { id } = useParams();
  const loc = useLocation();
  const isEdit = Boolean(id) && loc.pathname.includes("/edit");
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", role: "office",
    hourly_rate: 0, base_salary: 0, daily_hours: 8,
    address: "", emergency_contact: "", date_of_joining: "",
    password: "", scope_state_ids: [], scope_district_ids: [], scope_lsgd_ids: [], scope_zone_ids: [],
  });
  const [zones, setZones] = useState([]);
  const [locs, setLocs] = useState({ state: [], district: [], lsgd: [] });
  const [busy, setBusy] = useState(false);
  const [roles, setRoles] = useState([]);   // [{key, label, builtin}]

  // Load the full assignable-role list (built-in + tenant custom roles).
  useEffect(() => {
    api.get("/office-staff/available-roles")
      .then(r => setRoles(r.data.roles || []))
      .catch(() => {
        // Fallback so the form still works if the endpoint is unreachable.
        setRoles([
          { key: "office",  label: "Office Staff", builtin: true },
          { key: "manager", label: "Manager",      builtin: true },
          { key: "hr",      label: "HR",           builtin: true },
        ]);
      });
  }, []);

  useEffect(() => {
    api.get("/zones").then(r => setZones(r.data || [])).catch(() => {});
    ["state", "district", "lsgd"].forEach(t => api.get("/locations", { params: { type: t } })
      .then(r => setLocs(l => ({ ...l, [t]: (r.data || []).sort((a, b) => a.name.localeCompare(b.name)) }))).catch(() => {}));
  }, []);

  useEffect(() => {
    if (isEdit) {
      api.get(`/office-staff/${id}`).then(r => {
        setForm({
          name: r.data.name || "",
          email: r.data.email || "",
          phone: r.data.phone || "",
          role: r.data.role || "office",
          hourly_rate: r.data.hourly_rate || 0,
          base_salary: r.data.base_salary || 0,
          daily_hours: r.data.daily_hours ?? 8,
          address: r.data.address || "",
          emergency_contact: r.data.emergency_contact || "",
          date_of_joining: r.data.date_of_joining || "",
          password: "",
          scope_state_ids: r.data.scope_state_ids || [],
          scope_district_ids: r.data.scope_district_ids || (r.data.district_id ? [r.data.district_id] : []),
          scope_lsgd_ids: r.data.scope_lsgd_ids || [],
          scope_zone_ids: r.data.scope_zone_ids || (r.data.zone_id ? [r.data.zone_id] : []),
        });
      }).catch(e => toast.error(formatApiError(e)));
    }
  }, [isEdit, id]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        name: form.name, phone: form.phone || null, role: form.role,
        hourly_rate: Number(form.hourly_rate) || 0,
        base_salary: Number(form.base_salary) || 0,
        daily_hours: form.daily_hours === "" ? null : Number(form.daily_hours),
        address: form.address || null,
        emergency_contact: form.emergency_contact || null,
        date_of_joining: form.date_of_joining || null,
        scope_state_ids: form.scope_state_ids, scope_district_ids: form.scope_district_ids,
        scope_lsgd_ids: form.scope_lsgd_ids, scope_zone_ids: form.scope_zone_ids,
      };
      if (isEdit) {
        if (form.password) payload.password = form.password;
        await api.put(`/office-staff/${id}`, payload);
      } else {
        payload.email = form.email;
        payload.password = form.password || "ChangeMe@123";
        await api.post("/office-staff", payload);
      }
      toast.success(isEdit ? "Updated" : "Created");
      nav("/admin/staff-mgmt");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <AppShell title={isEdit ? "Edit Staff" : "Create Staff"} subtitle="Staff Management"
      actions={
        <button onClick={() => nav("/admin/staff-mgmt")}
                className="inline-flex items-center gap-2 h-10 px-3 border border-[#E5E2D9] rounded-md text-sm">
          <ArrowLeft size={14}/>Back
        </button>
      }>
      <form onSubmit={submit} className="max-w-2xl bg-white border border-[#E5E2D9] rounded-md p-6 grid grid-cols-2 gap-4" data-testid="office-staff-form">
        <F label="Name" value={form.name} onChange={v => setForm(f => ({...f, name: v}))} required testid="of-name"/>
        <F label="Email" type="email" value={form.email} onChange={v => setForm(f => ({...f, email: v}))} required disabled={isEdit} testid="of-email"/>
        <F label="Phone" value={form.phone} onChange={v => setForm(f => ({...f, phone: v}))} testid="of-phone"/>
        <div>
          <L>Role</L>
          <select data-testid="of-role" value={form.role} onChange={e => setForm(f => ({...f, role: e.target.value}))}
                  className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white">
            {(() => {
              const built = roles.filter(r => r.builtin);
              const custom = roles.filter(r => !r.builtin);
              return (
                <>
                  {built.length > 0 && (
                    <optgroup label="Built-in roles">
                      {built.map(r => (
                        <option key={r.key} value={r.key}>{r.label}</option>
                      ))}
                    </optgroup>
                  )}
                  {custom.length > 0 && (
                    <optgroup label="Custom roles">
                      {custom.map(r => (
                        <option key={r.key} value={r.key}>{r.label}</option>
                      ))}
                    </optgroup>
                  )}
                </>
              );
            })()}
          </select>
        </div>
        <F label={isEdit ? "New Password (optional)" : "Password"} type="password" value={form.password}
           onChange={v => setForm(f => ({...f, password: v}))} required={!isEdit} testid="of-password"/>
        <F label="Hourly Rate (₹)" value={form.hourly_rate} onChange={v => setForm(f => ({...f, hourly_rate: v}))} testid="of-hourly"/>
        <F label="Base Salary (₹)" value={form.base_salary} onChange={v => setForm(f => ({...f, base_salary: v}))}/>
        <F label="Daily Duty Hours" value={form.daily_hours} onChange={v => setForm(f => ({...f, daily_hours: v}))} testid="of-daily"/>
        <F label="Date of Joining" value={form.date_of_joining} onChange={v => setForm(f => ({...f, date_of_joining: v}))}/>
        <F label="Emergency Contact" value={form.emergency_contact} onChange={v => setForm(f => ({...f, emergency_contact: v}))}/>
        <div className="col-span-2 grid grid-cols-2 md:grid-cols-4 gap-4 p-3 rounded-lg bg-[#FAFAF7] border border-[#E5E2D9]" data-testid="of-area">
          <AreaMultiPick label="Assign State" testid="of-scope-state" options={locs.state} value={form.scope_state_ids}
            onChange={v => setForm(f => {
              const ds = v.length ? f.scope_district_ids.filter(id => v.includes(locs.district.find(d => d.id === id)?.parent_id)) : f.scope_district_ids;
              return { ...f, scope_state_ids: v, scope_district_ids: ds,
                scope_lsgd_ids: ds.length ? f.scope_lsgd_ids.filter(id => ds.includes(locs.lsgd.find(l => l.id === id)?.parent_id)) : f.scope_lsgd_ids };
            })} />
          <AreaMultiPick label="Assign District" testid="of-scope-district" value={form.scope_district_ids}
            options={form.scope_state_ids.length ? locs.district.filter(d => form.scope_state_ids.includes(d.parent_id)) : locs.district}
            onChange={v => setForm(f => ({...f, scope_district_ids: v,
              scope_lsgd_ids: v.length ? f.scope_lsgd_ids.filter(id => v.includes(locs.lsgd.find(l => l.id === id)?.parent_id)) : f.scope_lsgd_ids}))} />
          <AreaMultiPick label="Assign LSGD" testid="of-scope-lsgd" value={form.scope_lsgd_ids}
            options={form.scope_district_ids.length ? locs.lsgd.filter(l => form.scope_district_ids.includes(l.parent_id)) : locs.lsgd}
            onChange={v => setForm(f => ({...f, scope_lsgd_ids: v}))} />
          <AreaMultiPick label="Assign Zone" testid="of-scope-zone" options={zones} value={form.scope_zone_ids}
            onChange={v => setForm(f => ({...f, scope_zone_ids: v}))} />
          <p className="col-span-2 md:col-span-4 text-[11px] text-[#5C5A55]">
            Assigned area — "All" means no limit at that level. Managers see customer AI Service Analytics only for customers matching every level that is set.</p>
        </div>
        <div className="col-span-2">
          <L>Address</L>
          <textarea value={form.address} onChange={e => setForm(f => ({...f, address: e.target.value}))} rows={2}
                    className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md text-sm"/>
        </div>
        <button type="submit" disabled={busy} data-testid="of-save"
                className="col-span-2 h-10 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white rounded-md text-sm font-medium">
          {busy ? "Saving…" : isEdit ? "Update" : "Create staff"}
        </button>
      </form>
    </AppShell>
  );
}

const L = ({ children }) => (
  <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">{children}</label>
);
const F = ({ label, value, onChange, required, disabled, type = "text", testid }) => (
  <div>
    <L>{label}</L>
    <input data-testid={testid} type={type} required={required} disabled={disabled} value={value}
           onChange={e => onChange(e.target.value)}
           className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md disabled:bg-[#F7F6F2]"/>
  </div>
);
